/**
 * Мультипоиск — правила без React: что лежит в «Выбрано», в каких аптеках
 * есть всё сразу, кто «узкое место», если такой аптеки нет, и что уйдёт в
 * корзину. Наличие каждого лекарства собрано из одного ответа
 * `GET /catalog/availability?ids=`; пересечение считает экран.
 */
import type { Availability, Product } from '../catalog/api'
import type { Store } from '../stores/api'
import { distanceMeters, type Point } from '../stores/model'
import { canAddToCart } from '../catalog/view'

/** Выбранное лекарство: снимок из подсказки — список рисуется без запросов. */
export interface Picked {
  id: number
  name: string
  maker: string
  /** Цена каталога, сум; запасная, если аптека своей не прислала. */
  price: number
  rx: boolean
  inStock: boolean
  qty: number
}

/** Сколько штук можно выбрать — столько же принимает корзина. */
export const PICKED_MAX_QTY = 99

const clampQty = (qty: number) => Math.max(1, Math.min(PICKED_MAX_QTY, Math.round(qty)))

export function pick(product: Product): Picked {
  return {
    id: product.id,
    name: product.name,
    maker: product.maker,
    price: product.price,
    rx: product.rx,
    inStock: product.inStock,
    qty: 1,
  }
}

/** Добавить в конец; уже выбранное второй раз не добавляется. */
export function addPicked(list: Picked[], item: Picked): Picked[] {
  return list.some((p) => p.id === item.id) ? list : [...list, item]
}

export function removePicked(list: Picked[], id: number): Picked[] {
  return list.filter((p) => p.id !== id)
}

/** Количество от 1 до 99: ноль — это «убрать», для него есть своя кнопка. */
export function setPickedQty(list: Picked[], id: number, qty: number): Picked[] {
  return list.map((p) => (p.id === id ? { ...p, qty: clampQty(qty) } : p))
}

/**
 * Выбор из sessionStorage. Чужое или битое значение — пустой список, а не
 * падение экрана: хранилище браузера могли поправить руками.
 */
export function parsePicked(raw: string | null): Picked[] {
  try {
    const value: unknown = JSON.parse(raw ?? '[]')
    if (!Array.isArray(value)) return []
    return value.flatMap((item: Partial<Picked>) =>
      typeof item.id === 'number' && item.id > 0 && typeof item.name === 'string'
        ? [
            {
              id: item.id,
              name: item.name,
              maker: String(item.maker ?? ''),
              price: Number(item.price) || 0,
              rx: item.rx === true,
              inStock: item.inStock === true,
              qty: clampQty(Number(item.qty) || 1),
            },
          ]
        : [],
    )
  } catch {
    return []
  }
}

/** Аптека может отдать столько, сколько выбрано: `available` уже без брони (R-26). */
export function canGive(row: Availability, qty: number): boolean {
  return row.available >= qty
}

/** Наличие одного выбранного лекарства — ответ его запроса. */
export interface ItemStock {
  item: Picked
  rows: Availability[]
}

/** В скольких аптеках есть каждое выбранное — по нему видно «узкое место». */
export interface Coverage {
  item: Picked
  /** В скольких аптеках есть в нужном количестве. */
  stores: number
}

export function coverage(items: ItemStock[]): Coverage[] {
  return items.map(({ item, rows }) => ({
    item,
    stores: rows.filter((row) => canGive(row, item.qty)).length,
  }))
}

/** Аптека, где есть всё выбранное, и сколько это стоит в ней. */
export interface FullStore {
  storeId: number
  storeName: string
  /** Сумма за всё: цена аптеки, а если её нет — цена каталога. */
  total: number
}

/**
 * Аптеки, где каждое выбранное есть в нужном количестве. Порядок — как в
 * ответе о наличии первого лекарства; экран потом сортирует сам.
 */
export function storesWithAll(items: ItemStock[]): FullStore[] {
  const [first] = items
  if (!first) return []
  return first.rows.flatMap((row) => {
    let total = 0
    for (const { item, rows } of items) {
      const here = rows.find((r) => r.storeId === row.storeId && canGive(r, item.qty))
      if (!here) return []
      total += (here.price || item.price) * item.qty
    }
    return [{ storeId: row.storeId, storeName: row.storeName, total }]
  })
}

export type Match =
  | { kind: 'all'; stores: FullStore[] }
  /** Такой аптеки нет: `fewest` — лекарство, которое есть в меньшем числе аптек. */
  | { kind: 'none'; counts: Coverage[]; fewest: Coverage }

/** Пересечение наличия. Зовётся с непустым выбором: без лекарств и искать нечего. */
export function match(items: ItemStock[]): Match {
  const stores = storesWithAll(items)
  if (stores.length > 0) return { kind: 'all', stores }
  const counts = coverage(items)
  const fewest = counts.reduce((min, row) => (row.stores < min.stores ? row : min))
  return { kind: 'none', counts, fewest }
}

export interface RankedStore extends FullStore {
  store: Store
  meters: number | null
}

/**
 * Порядок списка «Где есть всё»: открытые сейчас — выше, среди них ближние
 * (если покупатель дал точку), дальше дешевле. Аптеки, которой нет в списке
 * сети, не показываем: без адреса и часов туда не дойти.
 */
export function rankStores(found: FullStore[], network: Store[], me: Point | null): RankedStore[] {
  return found
    .flatMap((row) => {
      const store = network.find((s) => s.id === row.storeId)
      if (!store) return []
      const meters =
        me && store.lat !== null && store.lng !== null
          ? distanceMeters(me, { lat: store.lat, lng: store.lng })
          : null
      return [{ ...row, store, meters }]
    })
    .sort(
      (a, b) =>
        Number(b.store.openNow) - Number(a.store.openNow) ||
        (a.meters ?? Infinity) - (b.meters ?? Infinity) ||
        a.total - b.total,
    )
}

/**
 * Что уйдёт в корзину по «Всё в корзину»: то же правило, что у кнопки «В
 * корзину» везде в приложении — рецептурное и то, чего нет в аптеках, нельзя
 * (R-11, R-10). Остальное покупатель видит отдельной строкой.
 */
export function cartLines(items: Picked[]): { productId: number; qty: number }[] {
  return items
    .filter((item) => canAddToCart({ rx: item.rx, inStock: item.inStock } as Product))
    .map((item) => ({ productId: item.id, qty: item.qty }))
}

export function skippedCount(items: Picked[]): number {
  return items.length - cartLines(items).length
}
